import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ChevronDown, Check, Search } from "lucide-react";
import { PlateCameraButton } from "@/components/PlateCameraButton";
import { PlateLookupButton } from "@/components/PlateLookupButton";

export const COLOR_OPTIONS = ["Branco", "Preto", "Prata", "Cinza", "Vermelho", "Azul", "Verde", "Bege", "Marrom", "Amarelo", "Laranja", "Dourado", "Vinho"];
const CURRENT_YEAR = new Date().getFullYear();
export const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR + 1 - 1980 + 1 }, (_, i) => CURRENT_YEAR + 1 - i);
const FIPE_BASE = "https://parallelum.com.br/fipe/api/v1/carros";

const _norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

// FIPE brand label fixes + alias search tokens.
// Keys are compared via _norm against the raw FIPE "nome".
const BRAND_FIXES = {
  "vw - volkswagen": { label: "Volkswagen", aliases: ["volks", "vw"] },
  "gm - chevrolet": { label: "Chevrolet", aliases: ["gm"] },
  "citro\u00ebn": { label: "Citro\u00ebn", aliases: ["citroen"] },
  "mercedes-benz": { label: "Mercedes-Benz", aliases: ["mercedes", "benz"] },
  "land rover": { label: "Land Rover", aliases: ["landrover"] },
  "alfa romeo": { label: "Alfa Romeo", aliases: [] },
  "aston martin": { label: "Aston Martin", aliases: [] },
  "rolls-royce": { label: "Rolls-Royce", aliases: ["rolls royce"] },
};

const PRIORITY = ["Volkswagen", "Chevrolet", "Fiat", "Ford", "Toyota", "Hyundai", "Honda", "Renault", "Jeep", "Nissan"];

// Fallback — códigos oficiais da FIPE (carros). Usado se a FIPE falhar ou vier incompleta.
const FALLBACK_BRANDS = [
  { codigo: 59, nome: "VW - VolksWagen" },
  { codigo: 23, nome: "GM - Chevrolet" },
  { codigo: 21, nome: "Fiat" },
  { codigo: 22, nome: "Ford" },
  { codigo: 56, nome: "Toyota" },
  { codigo: 26, nome: "Hyundai" },
  { codigo: 25, nome: "Honda" },
  { codigo: 48, nome: "Renault" },
  { codigo: 29, nome: "Jeep" },
  { codigo: 43, nome: "Nissan" },
  { codigo: 44, nome: "Peugeot" },
  { codigo: 13, nome: "Citro\u00ebn" },
  { codigo: 42, nome: "Mercedes-Benz" },
  { codigo: 12, nome: "BMW" },
  { codigo: 6, nome: "Audi" },
  { codigo: 41, nome: "Mitsubishi" },
  { codigo: 51, nome: "Suzuki" },
  { codigo: 60, nome: "Volvo" },
  { codigo: 31, nome: "Land Rover" },
];

function toTitleCase(s) {
  return (s || "").toLowerCase().split(/(\s|-|\/)/).map((p) => (/^[a-z\u00e0-\u00fc]/i.test(p) ? p.charAt(0).toUpperCase() + p.slice(1) : p)).join("");
}

function prettifyBrand(raw) {
  const key = _norm(raw).trim();
  const fix = BRAND_FIXES[key];
  if (fix) return { label: fix.label, aliases: fix.aliases };
  // Rule-of-thumb: if the raw name is ALL CAPS, title-case it; otherwise keep it as-is.
  const label = raw && raw === raw.toUpperCase() ? toTitleCase(raw) : raw;
  return { label, aliases: [] };
}

function sortBrands(list) {
  const prio = new Map(PRIORITY.map((n, i) => [_norm(n), i]));
  return [...list].sort((a, b) => {
    const ai = prio.has(_norm(a.label)) ? prio.get(_norm(a.label)) : null;
    const bi = prio.has(_norm(b.label)) ? prio.get(_norm(b.label)) : null;
    if (ai != null && bi != null) return ai - bi;
    if (ai != null) return -1;
    if (bi != null) return 1;
    return a.label.localeCompare(b.label, "pt-BR");
  });
}

function SearchCombo({ testid, placeholder, options, value, onPick, onPickOther, loading, allowOther = true }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const box = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    if (!open) { return undefined; }
    const away = (e) => { if (box.current && !box.current.contains(e.target)) { setOpen(false); setQ(""); } };
    document.addEventListener("pointerdown", away);
    const t = setTimeout(() => { try { box.current?.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* ignore */ } }, 250);
    return () => { document.removeEventListener("pointerdown", away); clearTimeout(t); };
  }, [open]);
  const filtered = useMemo(() => {
    const f = _norm(q);
    if (!f) { return options.slice(0, 200); }
    return options.filter((o) => _norm(o.label).includes(f) || (o.aliases || []).some((x) => _norm(x).includes(f))).slice(0, 200);
  }, [options, q]);
  const pick = (o) => { onPick(o); setOpen(false); setQ(""); inputRef.current?.blur(); };
  return (
    <div ref={box} className="relative mt-1" data-testid={`${testid}-combo`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none" />
        <input ref={inputRef} data-testid={testid} type="text" role="combobox" aria-expanded={open} autoComplete="off" autoCorrect="off" autoCapitalize="none" spellCheck={false}
          value={open ? q : value} placeholder={value || placeholder} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Enter" && filtered[0]) { e.preventDefault(); pick(filtered[0]); } if (e.key === "Escape") { setOpen(false); } }}
          style={{ fontSize: 16 }}
          className="w-full h-11 rounded-md border border-zinc-800 bg-zinc-900 pl-9 pr-9 text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-[var(--c-brand)]" />
        <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500 pointer-events-none transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div data-testid={`${testid}-list`} role="listbox" className="absolute left-0 right-0 top-full mt-1 z-40 rounded-md border border-zinc-800 bg-zinc-950 shadow-2xl max-h-[13.5rem] overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
          {loading ? (
            <div className="p-3 text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Carregando…</div>
          ) : !filtered.length ? (
            <div className="p-3 text-sm text-zinc-500">Nada encontrado</div>
          ) : filtered.map((o) => (
            <button key={o.key} type="button" data-testid={`${testid}-opt-${o.key}`} onClick={() => pick(o)}
              className={`w-full text-left px-3 h-10 text-sm flex items-center justify-between gap-2 hover:bg-zinc-900 ${o.top ? "font-semibold text-zinc-100" : "text-zinc-300"}`}>
              <span className="truncate">{o.label}</span>
              {value === o.label && <Check className="h-4 w-4 shrink-0" style={{ color: "var(--c-brand)" }} />}
            </button>
          ))}
          {allowOther && (
            <button type="button" data-testid={`${testid}-opt-other`} onClick={() => { onPickOther?.(); setOpen(false); setQ(""); }}
              className="sticky bottom-0 w-full text-left px-3 h-10 text-sm bg-zinc-950 border-t border-zinc-800 text-green-300 hover:bg-zinc-900">Outra (digitar)</button>
          )}
        </div>
      )}
    </div>
  );
}

export function VehicleForm({ value, onChange, idPrefix = "order-vehicle", onPlateRead }) {
  const [brandsRaw, setBrandsRaw] = useState([]);
  const [modelsRaw, setModelsRaw] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [brandMode, setBrandMode] = useState("select");
  const [modelMode, setModelMode] = useState("select");
  const [colorMode, setColorMode] = useState(value?.color && !COLOR_OPTIONS.includes(value.color) ? "other" : "select");

  const set = (patch) => onChange({ ...value, ...patch });

  useEffect(() => {
    let on = true;
    setLoadingBrands(true);
    axios.get(`${FIPE_BASE}/marcas`, { timeout: 8000, withCredentials: false })
      .then((r) => {
        if (!on) return;
        const list = Array.isArray(r.data) ? r.data : [];
        if (!list.length) { setBrandsRaw(FALLBACK_BRANDS); return; }
        const seen = new Set(list.map((b) => _norm(prettifyBrand(b.nome).label)));
        const extras = FALLBACK_BRANDS.filter((f) => !seen.has(_norm(prettifyBrand(f.nome).label)));
        setBrandsRaw([...list, ...extras]);
      })
      .catch(() => { if (on) setBrandsRaw(FALLBACK_BRANDS); })
      .finally(() => { if (on) setLoadingBrands(false); });
    return () => { on = false; };
  }, []);

  const brandOptions = useMemo(() => {
    const mapped = brandsRaw.map((b) => {
      const p = prettifyBrand(b.nome);
      return { key: String(b.codigo), codigo: b.codigo, label: p.label, aliases: p.aliases };
    });
    const byLabel = new Map();
    for (const o of mapped) if (!byLabel.has(_norm(o.label))) byLabel.set(_norm(o.label), o);
    const top = new Set(PRIORITY.map(_norm));
    return sortBrands(Array.from(byLabel.values())).map((o) => ({ ...o, top: top.has(_norm(o.label)) }));
  }, [brandsRaw]);

  const selectedBrand = useMemo(() => brandOptions.find((b) => b.label === value?.make), [brandOptions, value?.make]);

  useEffect(() => {
    if (!selectedBrand?.codigo) { setModelsRaw([]); return; }
    let on = true;
    setLoadingModels(true);
    axios.get(`${FIPE_BASE}/marcas/${selectedBrand.codigo}/modelos`, { timeout: 8000, withCredentials: false })
      .then((r) => { if (on) setModelsRaw(Array.isArray(r.data?.modelos) ? r.data.modelos : []); })
      .catch(() => { if (on) setModelsRaw([]); })
      .finally(() => { if (on) setLoadingModels(false); });
    return () => { on = false; };
  }, [selectedBrand?.codigo]);

  const modelOptions = useMemo(
    () => modelsRaw.map((m) => ({ key: String(m.codigo), label: m.nome, aliases: [] })).sort((a, b) => a.label.localeCompare(b.label, "pt-BR")),
    [modelsRaw],
  );

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label className="text-xs font-mono-caps text-zinc-400">Marca</Label>
        {brandMode === "select" && brandOptions.length > 0 ? (
          <SearchCombo
            testid={`${idPrefix}-make`}
            placeholder={loadingBrands ? "Carregando marcas…" : "Buscar marca…"}
            options={brandOptions}
            value={value?.make || ""}
            onPick={(o) => set({ make: o.label, model: "" })}
            onPickOther={() => { setBrandMode("other"); set({ make: "", model: "" }); }}
            loading={loadingBrands}
          />
        ) : (
          <Input data-testid={`${idPrefix}-make-input`} required value={value?.make || ""} onChange={(e) => set({ make: e.target.value })}
            style={{ fontSize: 16 }}
            className="mt-1 bg-zinc-900 border-zinc-800" placeholder={brandOptions.length ? "Digite a marca" : "Marca (API indisponível)"} />
        )}
      </div>
      <div className="col-span-2">
        <Label className="text-xs font-mono-caps text-zinc-400">Modelo</Label>
        {modelMode === "select" && modelOptions.length > 0 ? (
          <SearchCombo
            testid={`${idPrefix}-model`}
            placeholder={loadingModels ? "Carregando modelos…" : "Buscar modelo…"}
            options={modelOptions}
            value={value?.model || ""}
            onPick={(o) => set({ model: o.label })}
            onPickOther={() => { setModelMode("other"); set({ model: "" }); }}
            loading={loadingModels}
          />
        ) : (
          <Input data-testid={`${idPrefix}-model-input`} required value={value?.model || ""} onChange={(e) => set({ model: e.target.value })}
            style={{ fontSize: 16 }}
            className="mt-1 bg-zinc-900 border-zinc-800" placeholder="Modelo" />
        )}
      </div>
      <div className="col-span-2 grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400 whitespace-nowrap">Ano fabricação</Label>
          <Select value={value?.year ? String(value.year) : ""} onValueChange={(v) => set({ year: v ? Number(v) : "" })}>
            <SelectTrigger data-testid={`${idPrefix}-year`} className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Selecionar" /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-80">
              {YEAR_OPTIONS.map((y) => (<SelectItem key={y} value={String(y)} data-testid={`${idPrefix}-year-opt-${y}`}>{y}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400 whitespace-nowrap">Ano modelo</Label>
          <Select value={value?.year_model ? String(value.year_model) : ""} onValueChange={(v) => set({ year_model: v ? Number(v) : "" })}>
            <SelectTrigger data-testid={`${idPrefix}-year-model`} className="mt-1 bg-zinc-900 border-zinc-800"><SelectValue placeholder="Selecionar" /></SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-80">
              {YEAR_OPTIONS.map((y) => (<SelectItem key={y} value={String(y)} data-testid={`${idPrefix}-year-model-opt-${y}`}>{y}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="col-span-2 grid grid-cols-2 gap-3 items-start">
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Placa</Label>
          <div className="mt-1 flex gap-2">
          <Input
            data-testid={`${idPrefix}-plate`}
            required
            value={value?.plate || ""}
            onChange={(e) => {
              const clean = (e.target.value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
              set({ plate: clean });
            }}
            maxLength={7}
            minLength={7}
            autoCapitalize="characters"
            autoCorrect="off"
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            pattern="[A-Z]{3}[0-9]{4}|[A-Z]{3}[0-9][A-Z][0-9]{2}"
            title="Placa inválida. Use ABC1234 (antiga) ou ABC1D23 (Mercosul)."
            style={{ fontSize: 16 }}
            className="bg-zinc-900 border-zinc-800 uppercase tracking-widest"
            placeholder="ABC1D23"
          />
          <PlateCameraButton testid={`${idPrefix}-plate-camera`} onRead={(p) => (onPlateRead ? onPlateRead(p) : set({ plate: p }))} />
          </div>
        </div>
        <div>
          <Label className="text-xs font-mono-caps text-zinc-400">Cor</Label>
          {colorMode === "select" ? (
            <SearchCombo
              testid={`${idPrefix}-color-select`}
              placeholder="Buscar cor…"
              options={COLOR_OPTIONS.map((c) => ({ key: c.toLowerCase(), label: c }))}
              value={value?.color || ""}
              onPick={(o) => set({ color: o.label })}
              onPickOther={() => { setColorMode("other"); set({ color: "" }); }}
            />
          ) : (
            <Input data-testid={`${idPrefix}-color-input`} value={value?.color || ""} onChange={(e) => set({ color: e.target.value })}
              style={{ fontSize: 16 }} className="mt-1 bg-zinc-900 border-zinc-800" placeholder="Cor" />
          )}
        </div>
      </div>
      <div className="col-span-2">
        <PlateLookupButton plate={value?.plate || ""} onFound={(d) => set({ make: d.make || value?.make, model: d.model || value?.model, year: d.year || value?.year, year_model: d.year_model || value?.year_model, color: d.color || value?.color, chassi: d.chassi || value?.chassi })} />
        {value?.plate && value.plate.length === 7 && !/^([A-Z]{3}[0-9]{4}|[A-Z]{3}[0-9][A-Z][0-9]{2})$/.test(value.plate) && (
          <p data-testid={`${idPrefix}-plate-error`} className="text-[11px] text-red-400 mt-1">Placa inválida. Use ABC1234 ou ABC1D23.</p>
        )}
      </div>
      <div className="col-span-2">
        <Label className="text-xs font-mono-caps text-zinc-400">Chassi (opcional)</Label>
        <Input data-testid={`${idPrefix}-chassi`} value={value?.chassi || ""} onChange={(e) => set({ chassi: e.target.value.toUpperCase() })} maxLength={17}
          style={{ fontSize: 16 }} className="mt-1 bg-zinc-900 border-zinc-800 font-mono uppercase" placeholder="17 caracteres" />
      </div>
      {(loadingBrands || loadingModels) && (
        <div className="col-span-2 text-xs text-zinc-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Carregando lista FIPE…</div>
      )}
    </div>
  );
}
